import React, { useContext, useMemo, useState } from 'react'
import { CaseDetailContext } from './CaseDetailContext'
import PaymentActionMenu from '../shared/PaymentActionMenu'
import api from '../../utils/api'
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../shared/tableUtils'

// Paper-plane, rotated to match the mediator portal's held-row control.
const SendIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
)

const DownloadIcon = ({ color = '#115e59' }) => (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.75 8.75v3.5h10.5v-3.5" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.08 5.83L7 8.75" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9.92 5.83L7 8.75" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 1.75v7" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

/**
 * FinanceTable — port of Scale's FinanceTable matching the screenshot.
 *
 * Columns: INVOICED TO | PARTY NAME | PAYMENT DATE |
 *          AMOUNT | INVOICE | STATUS | (actions menu)
 *
 * Empty state: folder icon + "No Record Found".
 */

const STATUS_BG = {
    paid: 'bg-green-50 text-green-700 border-green-200',
    paid_manually: 'bg-green-50 text-green-700 border-green-200',
    pending: 'bg-red-50 text-red-700 border-red-200',
    pending_approval: 'bg-blue-50 text-blue-700 border-blue-200',
    due: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    no_payment: 'bg-gray-50 text-gray-600 border-gray-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
}

// Match Scale's exact pill labels.
function pillLabel(rawStatus, amount, dueDate) {
    const s = (rawStatus || '').toLowerCase()
    if (Number(amount || 0) <= 0) return { key: 'no_payment', label: 'No Payment' }
    if (s === 'paid') return { key: 'paid', label: 'Paid by Stripe' }
    if (s === 'paid_manually') return { key: 'paid_manually', label: 'Paid Manually' }
    if (s === 'pending_approval') return { key: 'pending_approval', label: 'Pending Approval' }
    // Due-soon: within 5 days of dueDate
    if (dueDate) {
        const d = new Date(dueDate)
        if (!isNaN(d.getTime())) {
            const days = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            if (days >= 0 && days <= 5) return { key: 'due', label: `Due in ${days === 0 ? 'today' : days + (days === 1 ? ' day' : ' days')}` }
        }
    }
    if (s === 'pending') return { key: 'pending', label: 'Not Paid' }
    return { key: s || 'pending', label: s ? s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Not Paid' }
}

function StatusPill({ rawStatus, amount, dueDate }) {
    const { key, label } = pillLabel(rawStatus, amount, dueDate)
    const cls = STATUS_BG[key] || STATUS_BG.pending
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>
}

function fmtDate(s) {
    if (!s) return '-'
    // "May 04, 2026" — matches header date format.
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })
}

function fmtMoney(n) {
    const v = Number(n || 0)
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

// Scale parity (Scale FinanceTable.jsx): the "Invoiced to" column shows the
// party NAME, and the "Party Name" column shows the party TYPE — Plaintiff /
// Defendant, or "FLA Lawyer Team" for GSC cases. Returns { name, type }.
function partyInfoFor(payment, caseData) {
    const cid = payment.clientId || payment.client_id
    const pid = caseData?.plaintiff?.client_id || caseData?.plaintiff?.clientId
    const did = caseData?.defendant?.client_id || caseData?.defendant?.clientId
    const isGsc = caseData?.is_gsc_case === true || payment.is_gsc_case === true
    let name = payment.invoiceCustomerName || payment.invoice_customer_name || ''
    let type = '-'
    if (cid && pid === cid) {
        type = 'Plaintiff'
        name = caseData?.plaintiff?.name || name
    } else if (cid && did === cid) {
        type = 'Defendant'
        name = caseData?.defendant?.name || name
    } else if (isGsc) {
        type = 'FLA Lawyer Team'
    }
    return { name: name || '-', type }
}

// "Mediation Date" cell text — matches Scale: "Month DD, YYYY - <slot>".
function fmtMediation(caseData) {
    const d = caseData?.mediation_date || caseData?.date
    if (!d) return '-'
    const slot = caseData?.slot_type || caseData?.slotType
    return `${fmtDate(d)}${slot ? ` - ${slot}` : ''}`
}

export default function FinanceTable({ onToast, onRefresh, mediatorUserId }) {
    const { caseData } = useContext(CaseDetailContext)
    const [invoiceLoader, setInvoiceLoader] = useState(null)
    // Manual invoice approval (Turner ADR). Their mediators hold every invoice
    // until a human releases it, so a case manager needs the same letter icon
    // the mediator portal has had all along (FinanceTable.jsx status === 'held').
    const [approveLoader, setApproveLoader] = useState(null)
    const [confirmSendId, setConfirmSendId] = useState(null)

    // Approve a held invoice and email it to the parties. On success the row
    // leaves 'held' and becomes "Approved & Sent", so refresh the case rather
    // than patching the row — the finance table is derived from caseData.
    const handleApproveAndSend = async (paymentId) => {
        if (approveLoader) return
        setApproveLoader(paymentId)
        try {
            const { data } = await api.post(`/cases/payments/${paymentId}/approve-send`)
            // scale-be answers 200 with alreadySent:true when the row was not
            // actually held — a double click, or someone else released it first.
            // Say so plainly instead of claiming we just sent an email.
            if (data?.data?.alreadySent) {
                onToast?.({ type: 'warning', message: 'This invoice was already sent' })
            } else {
                onToast?.({ type: 'success', message: 'Invoice approved and emailed to the parties' })
            }
            onRefresh?.()
        } catch (err) {
            onToast?.({
                type: 'error',
                message:
                    err?.response?.data?.message ||
                    'Could not send the invoice. Please try again.',
            })
        } finally {
            setApproveLoader(null)
            setConfirmSendId(null)
        }
    }

    // MVP coupon detection — mirrors Scale's FinanceTable. When the case was
    // created with an MVP coupon and no invoice was generated, download is off.
    const isMvpCouponApplied = useMemo(() => {
        const couponCode =
            caseData?.coupon?.coupon_code ||
            caseData?.coupon_id?.coupon_code ||
            caseData?.coupon_code ||
            ''
        return String(couponCode).trim().toUpperCase() === 'MVP'
    }, [caseData?.coupon?.coupon_code, caseData?.coupon_id?.coupon_code, caseData?.coupon_code])

    // Download/generate the invoice — same flow as Scale (and CM's Billing page):
    // regular payments with no Stripe invoice_id open invoice_url/payment_url
    // directly; Stripe-linked invoices go through /stripe/payment-link (proxied
    // by the CM backend), with type 'link' when paid, else 'pdf'.
    const generateInvoiceUrl = async (p) => {
        if (invoiceLoader) return
        const status = p.rawStatus || p.status
        const invoiceId = p.invoiceId ?? p.invoice_id
        if (!invoiceId) {
            const url = (status === 'paid' || status === 'paid_manually')
                ? (p.paymentUrl || p.invoiceUrl)
                : p.invoiceUrl
            if (url) window.open(url, '_blank')
            return
        }
        setInvoiceLoader(p.id)
        try {
            const type = (status === 'paid' || status === 'paid_manually') ? 'link' : 'pdf'
            const res = await api.post(`/cases/payments/${p.id}/stripe-link`, {
                mediator_user_id: mediatorUserId,
                type,
                source: p.source,
            })
            const url = res?.data?.data?.url || res?.data?.data?.invoice_url || res?.data?.data?.payment_url
            if (url) window.open(url, '_blank')
            else if (onToast) onToast({ type: 'error', message: 'Could not generate invoice link' })
        } catch (err) {
            console.error('Invoice URL generation failed:', err)
            if (onToast) onToast({ type: 'error', message: 'Failed to generate invoice link' })
        } finally {
            setInvoiceLoader(null)
        }
    }
    // Tolerate the BE returning payments as null / object / non-array.
    const payments = useMemo(() => {
        if (Array.isArray(caseData?.payments)) return caseData.payments
        if (Array.isArray(caseData?.plaintiffPayments) || Array.isArray(caseData?.defendentPayments)) {
            return [
                ...(Array.isArray(caseData.plaintiffPayments) ? caseData.plaintiffPayments : []),
                ...(Array.isArray(caseData.defendentPayments) ? caseData.defendentPayments : []),
            ]
        }
        return []
    }, [caseData])
    const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
        'caseDetail.finance.v2',
        { invoicedTo: 180, party: 140, paymentDate: 150, mediationDate: 190, amount: 120, invoice: 110, status: 160 }
    )
    const rows = useMemo(
        () => payments.map((p) => ({ ...p, _ps: partyInfoFor(p, caseData) })),
        [payments, caseData]
    )
    const { sortedRows: sortedPayments, sort: tableSort, requestSort: requestTableSort } = useSortableData(
        rows,
        {
            invoicedTo: (r) => r._ps.name,
            party: (r) => r._ps.type,
            paymentDate: (r) => r.paidAt || '',
            mediationDate: () => caseData?.mediation_date || caseData?.date || '',
            amount: (r) => Number(r.grandTotal || r.amount || 0),
            invoice: (r) => r.invoiceNumber || '',
            status: (r) => r.rawStatus || r.status || '',
        }
    )

    if (payments.length === 0) {
        // One-line console hint — only fires when no payments are found, so
        // you can quickly see in DevTools what the BE actually sent.
        // eslint-disable-next-line no-console
        if (caseData) console.debug('[FinanceTable] no invoices to show. caseData.payments=', caseData.payments, 'plaintiffPayments=', caseData.plaintiffPayments, 'defendentPayments=', caseData.defendentPayments)
        return (
            <div className="px-5 py-12 flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                    <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                </div>
                <p className="text-sm font-semibold text-gray-700">No Record Found</p>
                <p className="text-xs text-gray-500">No invoices have been created for this case yet.</p>
            </div>
        )
    }

    const headerCellCls = 'px-4 py-3 text-left font-semibold text-gray-700 text-xs uppercase tracking-wide relative'
    return (
        <div className="overflow-auto">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <thead className="border-b border-gray-200">
                    <tr>
                        <th className={headerCellCls} style={{ width: colWidths.invoicedTo }}>
                            <SortableHeader sortKey="invoicedTo" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Invoiced To</SortableHeader>
                            <ResizeHandle onResize={setColWidth('invoicedTo')} getCurrentWidth={() => colWidths.invoicedTo} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.party }}>
                            <SortableHeader sortKey="party" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Party Name</SortableHeader>
                            <ResizeHandle onResize={setColWidth('party')} getCurrentWidth={() => colWidths.party} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.paymentDate }}>
                            <SortableHeader sortKey="paymentDate" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Payment Date</SortableHeader>
                            <ResizeHandle onResize={setColWidth('paymentDate')} getCurrentWidth={() => colWidths.paymentDate} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.mediationDate }}>
                            <SortableHeader sortKey="mediationDate" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Mediation Date</SortableHeader>
                            <ResizeHandle onResize={setColWidth('mediationDate')} getCurrentWidth={() => colWidths.mediationDate} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.amount }}>
                            <SortableHeader sortKey="amount" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Amount</SortableHeader>
                            <ResizeHandle onResize={setColWidth('amount')} getCurrentWidth={() => colWidths.amount} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.invoice }}>
                            <SortableHeader sortKey="invoice" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Invoice</SortableHeader>
                            <ResizeHandle onResize={setColWidth('invoice')} getCurrentWidth={() => colWidths.invoice} />
                        </th>
                        <th className={headerCellCls} style={{ width: colWidths.status }}>
                            <SortableHeader sortKey="status" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase tracking-wide font-semibold text-gray-700">Status</SortableHeader>
                            <ResizeHandle onResize={setColWidth('status')} getCurrentWidth={() => colWidths.status} />
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 text-xs uppercase tracking-wide">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {sortedPayments.map((p) => {
                        const amount = p.grandTotal || p.amount || 0
                        const ps = p._ps
                        return (
                            <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-900 truncate">{ps.name}</td>
                                <td className="px-4 py-3 text-gray-700 truncate capitalize">{ps.type}</td>
                                <td className="px-4 py-3 text-gray-700 truncate">{p.paidAt ? fmtDate(p.paidAt) : '-'}</td>
                                <td className="px-4 py-3 text-gray-700 truncate capitalize">{fmtMediation(caseData)}</td>
                                <td className="px-4 py-3 text-gray-900 font-medium">{fmtMoney(amount)}</td>
                                <td className="px-4 py-3 truncate">
                                    {(() => {
                                        // Same conditions as Scale's FinanceTable invoice column.
                                        const invoiceLink = p.invoiceUrl
                                        const displayAmount = (p.grandTotal ?? p.amount ?? 0)
                                        const disableInvoiceDownload =
                                            (isMvpCouponApplied && !invoiceLink) || Number(displayAmount) === 0
                                        const enabled = invoiceLink && !disableInvoiceDownload
                                        const title = Number(displayAmount) === 0
                                            ? 'Invoice download is disabled when amount is $0'
                                            : disableInvoiceDownload
                                                ? 'Invoice download is disabled for MVP coupon cases applied at creation'
                                                : invoiceLink
                                                    ? 'Download Invoice'
                                                    : 'Invoice will be generated upon completion of onboarding form.'
                                        const loading = invoiceLoader === p.id
                                        return (
                                            <button
                                                type="button"
                                                title={title}
                                                disabled={!enabled || loading}
                                                onClick={() => enabled && generateInvoiceUrl(p)}
                                                className={`inline-flex items-center gap-1.5 text-sm ${enabled ? 'text-teal-600 cursor-pointer hover:underline' : 'text-gray-400 cursor-not-allowed'}`}
                                                style={{ background: 'transparent', border: 'none', padding: 0 }}
                                            >
                                                {loading ? (
                                                    <span className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <DownloadIcon color={enabled ? '#0d9488' : '#9ca3af'} />
                                                )}
                                                <span>Invoice</span>
                                            </button>
                                        )
                                    })()}
                                </td>
                                <td className="px-4 py-3">
                                    {String(p.rawStatus || p.status || '').toLowerCase() === 'held' ? (
                                        // Mirrors the mediator portal: on a held row the
                                        // ACTION replaces the badge, because "held" is not
                                        // a state the reader needs describing — it is a
                                        // thing waiting to be done.
                                        <button
                                            type="button"
                                            title="Approve & Send"
                                            aria-label="Approve and send invoice"
                                            disabled={approveLoader !== null && approveLoader !== p.id}
                                            onClick={() => approveLoader === null && setConfirmSendId(p.id)}
                                            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-teal-50 hover:text-teal-600 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            {approveLoader === p.id ? (
                                                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
                                            ) : (
                                                <SendIcon />
                                            )}
                                        </button>
                                    ) : (
                                        <StatusPill rawStatus={p.rawStatus || p.status} amount={amount} dueDate={p.dueDate} />
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <PaymentActionMenu payment={p} mediatorUserId={mediatorUserId} chequeImageUrl={p.chequeImageUrl || p.cheque_image_url || null} onToast={onToast} onChanged={onRefresh} />
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            {/* Approve & Send confirmation. This sends a real email to the
                parties and cannot be taken back, so it asks first — same as the
                mediator portal. */}
            {confirmSendId !== null && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
                    onClick={() => approveLoader === null && setConfirmSendId(null)}
                >
                    <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <h5 className="mb-2 text-lg font-semibold text-gray-900">Approve &amp; send this invoice?</h5>
                        <p className="mb-5 text-sm text-gray-600">
                            The invoice will be emailed to the parties on this case straight away.
                            This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                disabled={approveLoader !== null}
                                onClick={() => setConfirmSendId(null)}
                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={approveLoader !== null}
                                onClick={() => handleApproveAndSend(confirmSendId)}
                                className="rounded-md bg-[#115E59] px-4 py-2 text-sm font-medium text-white hover:bg-[#0d4a46] disabled:opacity-50"
                            >
                                {approveLoader !== null ? 'Sending…' : 'Approve & Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
